import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { getConfluencePageAdfFromApi } from '@atlassian/ai-issue-create/services/get-confluence-page/utils';
import type {
	GetConfluencePage,
	ContentType as AiIssueCreateContentType,
} from '@atlassian/ai-issue-create/types';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';

import { PageMode, type ContentType } from '../providers/IssueCreateSidePanelContextProvider.tsx';

import { useAiIssueCreateAnalytics } from './useAiIssueCreateAnalytics.tsx';
import { getConfluencePageAdfFromEditor } from './getConfluencePageUtils.ts';
type UseGetConfluencePage = Omit<ExtendedGetConfluencePage, 'signal'> & {
	onStateChange: (state: GetConfluencePageState, error?: Error | unknown) => void;
};

type AbortState = { state: 'abort'; confluencePage: undefined };
type LoadingState = { state: 'loading'; confluencePage: undefined };
type SuccessState = { state: 'idle'; confluencePage: DocNode };
type ErrorState = { state: 'error'; error: Error | unknown; confluencePage: undefined };
type GetConfluencePageState = AbortState | LoadingState | ErrorState | SuccessState;
type ExtendedGetConfluencePage = Omit<GetConfluencePage, 'contentType'> & {
	contentType?: ContentType;
	pageMode?: PageMode;
	editorApi?: FullPageEditorPresetAPI;
};

const getConfluencePageAdf = async ({
	cloudId,
	contentType,
	pageId,
	pageMode = PageMode.VIEW,
	signal,
	product = 'confluence',
	experienceId = 'ai-issue-create',
	editorApi,
}: ExtendedGetConfluencePage): Promise<DocNode | undefined> => {
	if (
		pageMode === PageMode.EDIT ||
		pageMode === PageMode.LIVE_EDIT ||
		pageMode === PageMode.LIVE_VIEW
	) {
		return getConfluencePageAdfFromEditor(editorApi);
	}
	// 'folder' is not a supported content type in ai-issue-create; fall back to no contentType
	const apiContentType =
		contentType !== 'folder' ? (contentType as AiIssueCreateContentType) : ('page' as const);
	return getConfluencePageAdfFromApi({
		cloudId,
		contentType: apiContentType,
		pageId,
		signal,
		product,
		experienceId,
	});
};

export const useGetConfluencePage = ({
	cloudId,
	contentType,
	experienceId,
	pageId,
	pageMode,
	product,
	onStateChange,
}: UseGetConfluencePage): Omit<GetConfluencePageState, 'signal'> & {
	trigger: () => Promise<void>;
} => {
	const { fireTrack } = useAiIssueCreateAnalytics();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const [fetchState, setGetConfluencePageState] = useState<GetConfluencePageState>({
		state: 'loading',
		confluencePage: undefined,
	});

	const abortControllerRef = useRef<AbortController | null>(null);

	const fetchConfluencePage = useCallback(async () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		abortControllerRef.current = new AbortController();
		const currentAbortController = abortControllerRef.current;

		setGetConfluencePageState({ state: 'loading', confluencePage: undefined });

		try {
			fireTrack('issueCreateSidePanelGetConfluencePageRequest start');
			const confluencePage = await getConfluencePageAdf({
				cloudId,
				contentType,
				experienceId,
				pageId,
				pageMode,
				product,
				signal: currentAbortController.signal,
				editorApi: fullPageEditorPresetAPI,
			});
			fireTrack('issueCreateSidePanelGetConfluencePageRequest complete', {
				haveResult: !!confluencePage,
			});

			if (currentAbortController.signal.aborted) {
				setGetConfluencePageState({ state: 'abort', confluencePage: undefined });
				return;
			}

			if (confluencePage) {
				setGetConfluencePageState({ state: 'idle', confluencePage });
			} else {
				setGetConfluencePageState({
					state: 'error',
					error: new Error('Error on getting confluence page as ADF'),
					confluencePage: undefined,
				});
			}
		} catch (e: Error | unknown) {
			fireTrack('issueCreateSidePanelGetConfluencePageRequest fail', {
				message: (e as Error).message,
			});
			if (currentAbortController.signal.aborted) {
				setGetConfluencePageState({ state: 'abort', confluencePage: undefined });
				return;
			}

			setGetConfluencePageState({ state: 'error', error: e, confluencePage: undefined });
		}
	}, [
		cloudId,
		contentType,
		experienceId,
		pageId,
		product,
		setGetConfluencePageState,
		fireTrack,
		pageMode,
		fullPageEditorPresetAPI,
	]);

	useEffect(() => {
		void fetchConfluencePage();

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [fetchConfluencePage]);

	useEffect(() => {
		if (onStateChange) {
			onStateChange(fetchState);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchState.state, fetchState.confluencePage, onStateChange]);

	return { ...fetchState, trigger: fetchConfluencePage };
};
